import React, { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import LoadingSpinner from './LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFile, faLink } from '@fortawesome/free-solid-svg-icons';

const { REACT_APP_AIRTABLE_API_TOKEN } = process.env;

function PubCard() {
    const [showModal, setShowModal] = useState(false);
    const [abstract, setAbstract] = useState('');
    const [selectedPub, setSelectedPub] = useState(null);
    const [animationIndex, setAnimationIndex] = useState(0);
    const [highlightedPubs, setHighlightedPubs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(
                    "https://api.airtable.com/v0/appXqP56p5bXn4bKa/Featured%20Publications?view=Grid%20view",
                    {
                        headers: {
                            'Authorization': `Bearer ${REACT_APP_AIRTABLE_API_TOKEN}`,
                        },
                    }
                );
                const responseData = await response.json();
                // Map Airtable records to the expected format
                const pubs = responseData.records.map(record => {
                    // Handle attachment field - Img is an array of attachment objects
                    const imgAttachment = record.fields.Img && Array.isArray(record.fields.Img) && record.fields.Img.length > 0 
                        ? record.fields.Img[0].url 
                        : '';
                    
                    // Handle PDF field - array of attachment objects
                    const pdfAttachment = record.fields.Pdf && Array.isArray(record.fields.Pdf) && record.fields.Pdf.length > 0
                        ? record.fields.Pdf[0].url
                        : '';
                    
                    // Handle Additional PDFs - array of attachment objects
                    const additionalPDFs = record.fields['AdditionalPDFs'] && Array.isArray(record.fields['AdditionalPDFs'])
                        ? record.fields['AdditionalPDFs'].map(attachment => ({
                            url: attachment.url,
                            filename: attachment.filename || 'PDF'
                        }))
                        : [];
                    
                    // Handle Additional Links - comma-separated text
                    const additionalLinks = record.fields['AdditionalLinks'] && typeof record.fields['AdditionalLinks'] === 'string'
                        ? record.fields['AdditionalLinks'].split(',').map(link => link.trim()).filter(link => link.length > 0)
                        : [];
                    
                    return {
                        title: record.fields.Title || '',
                        authors: record.fields.Authors || '',
                        abstract: record.fields.Abstract || '',
                        JournalURL: record.fields.JournalURL || '',
                        download: pdfAttachment,
                        img: imgAttachment,
                        alt: record.fields.ImgAlt || '',
                        journal: record.fields.Journal || '',
                        miscInfo: record.fields.MiscInfo || '',
                        additionalPDFs: additionalPDFs,
                        additionalLinks: additionalLinks
                    };
                });
                setHighlightedPubs(pubs);
            } catch (error) {
                console.error('Error fetching data: ', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (highlightedPubs.length > 0) {
            const interval = setInterval(() => {
                setAnimationIndex(prevIndex => {
                    if (prevIndex === highlightedPubs.length - 1) {
                        clearInterval(interval); // Stop interval after all cards have animated in
                    }
                    return prevIndex + 1;
                });
            }, 200); // Adjust the delay between each card animation as needed
            return () => clearInterval(interval); // Clear interval on component unmount
        }
    }, [highlightedPubs.length]);

    const handleShowModal = (abstract, pub) => {
        setAbstract(abstract);
        setSelectedPub(pub);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const [expandedAuthors, setExpandedAuthors] = useState([]);

    useEffect(() => {
        setExpandedAuthors(Array(highlightedPubs.length).fill(false));
    }, [highlightedPubs.length]);

    const handleToggleAuthors = (index) => {
        const newExpandedAuthors = [...expandedAuthors];
        newExpandedAuthors[index] = !newExpandedAuthors[index];
        setExpandedAuthors(newExpandedAuthors);
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="card-container">
            {highlightedPubs.map((pub, index) => (
                <Card key={index} className={`custom-card ${index <= animationIndex ? 'fade-in' : ''}`}>
                    <div className="card-img-wrapper">
                        <Card.Img src={pub.img} alt={pub.alt} />
                    </div>
                    <Card.Body>
                        <Card.Title>
                            <a target="_blank" rel="noopener noreferrer" href={pub.JournalURL}>
                                {pub.title}
                            </a>
                        </Card.Title>
                        <Card.Text className="card-text">
                            <div className="journal-info"><i>{pub.journal}</i>, {pub.miscInfo}</div>
                            <div className="authors-info">
                                {pub.authors && typeof pub.authors === 'string' && pub.authors.trim() && (expandedAuthors[index] || pub.authors.split(',').length <= 3 ? (
                                    pub.authors
                                ) : (
                                    <>
                                        {pub.authors.split(',').slice(0, 3).join(', ')}
                                        {' '}
                                        <Button
                                            variant="link"
                                            onClick={() => handleToggleAuthors(index)}
                                            className="expand-button"
                                        >
                                            [ + ]
                                        </Button>
                                    </>
                                ))}
                                {pub.authors && typeof pub.authors === 'string' && pub.authors.trim() && expandedAuthors[index] && (
                                    <>
                                        <br/>
                                        <Button
                                            variant="link"
                                            onClick={() => handleToggleAuthors(index)}
                                            className="collapse-button"
                                        >
                                            [ - ]
                                        </Button>
                                    </>
                                )}
                            </div>
                        </Card.Text>
                        <div className="button-div">
                            <Button
                                className="btn-secondary"
                                variant="primary"
                                onClick={() => handleShowModal(pub.abstract, pub)} // Pass the pub object
                            >
                                Read Abstract
                            </Button>
                            {pub.download && (
                                <Button
                                    className="btn-pdf"
                                    variant="primary"
                                    href={pub.download}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon icon={faFilePdf} />
                                </Button>
                            )}
                        </div>
                        {(pub.additionalPDFs.length > 0 || pub.additionalLinks.length > 0) && (
                            <div className="additional-materials-section">
                                <h6 className="additional-materials-heading">
                                    Additional Materials
                                </h6>
                                <div className="additional-materials-tags">
                                    {pub.additionalPDFs.map((pdf, pdfIndex) => (
                                        <Button
                                            key={`pdf-${pdfIndex}`}
                                            className="btn-tag"
                                            variant="outline-secondary"
                                            href={pdf.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FontAwesomeIcon icon={faFile} /> {pdf.filename}
                                        </Button>
                                    ))}
                                    {pub.additionalLinks.map((link, linkIndex) => {
                                        // Ensure link is absolute (starts with http:// or https://)
                                        const absoluteLink = link.startsWith('http://') || link.startsWith('https://') 
                                            ? link 
                                            : `https://${link}`;
                                        
                                        return (
                                            <Button
                                                key={`link-${linkIndex}`}
                                                className="btn-tag"
                                                variant="outline-secondary"
                                                href={absoluteLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FontAwesomeIcon icon={faLink} /> {link}
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            ))}
            {selectedPub && (
                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title><b>{selectedPub.title}</b></Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{abstract}</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}

export default PubCard;
